// Lightweight wrapper around the gtag.js snippet loaded in public/index.html.
// Safe to call before gtag is ready or when it's been blocked (ad blockers,
// tracking protection) — every helper no-ops silently in that case so the app
// can never break because of analytics.
//
// Routing here is state-based (App.js useState("splash"/"login"/...)), so the
// URL never changes. send_page_view is disabled on the gtag config and we fire
// page_view manually from App.js whenever the page state changes.

function gtagSafe(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try { window.gtag(...args); } catch { /* swallow — never break the app for analytics */ }
}

// Friendly page titles for the page state strings used in App.js.
// Add a new entry here when a new page state is introduced.
const PAGE_TITLES = {
  splash: "Splash",
  login: "Login",
  onboarding: "Onboarding",
  mode: "Home",
  settings: "Settings",
  pro: "Pricing",
  explain: "Explain",
  app: "AI Tutor",
  test: "Test",
  dashboard: "Dashboard",
  flashcards: "Flash Cards",
  examcorner: "Exam Corner",
};

/**
 * Fire a manual page_view event for the current state-route. Call from a
 * useEffect that watches the `page` state in App.js.
 */
export function trackPage(page) {
  if (!page) return;
  const title = PAGE_TITLES[page] || page;
  const path = "/" + page;
  const origin = (typeof window !== "undefined" && window.location?.origin) || "";
  // GA4 ignores the explicit page_title param for page_view events and reads
  // document.title instead, so we update it. Side-benefit: the browser tab
  // also reflects the current route ("Samajh — Home", "Samajh — Login", etc.).
  if (typeof document !== "undefined") {
    document.title = `Samajh — ${title}`;
  }
  gtagSafe("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: origin + path,
  });
}

/**
 * Attach a stable user identifier to subsequent events. Use the Firebase UID
 * (an opaque random string) — never email or name. Lets GA's User Explorer
 * correlate sessions across devices for the same student.
 */
export function setUserId(uid) {
  if (!uid) return;
  gtagSafe("set", { user_id: uid });
}

/**
 * Fire a custom event. Use for milestones — signup_completed, chapter_started,
 * chapter_completed, mastery_level_up, etc. Params should be plain serializable.
 */
export function trackEvent(name, params = {}) {
  if (!name) return;
  gtagSafe("event", name, params);
}
